/* Release the page after the Klaviyo signup popup closes.

   Klaviyo locks the page while its modal is open, by putting
   .klaviyo-prevent-body-scrolling on <body> (and sometimes an inline
   overflow:hidden), and it can leave the lock behind on close — the modal
   goes, the page stays frozen. A full-viewport layer of theirs outliving the
   modal does the same thing by swallowing clicks.

   So this checks one thing: is a Klaviyo modal actually on screen? If not,
   nothing is allowed to keep the page locked. It runs after their own
   open/close events, and whenever something changes the <body> class or adds
   and removes nodes at the end of <body>, which is where their layers mount.

   Klaviyo's close is a fade-out: the overlay animates to opacity 0 and is
   supposed to unmount when the animation ends. On this store it stays
   mounted -- invisible, full-screen, z-index 90000 -- so it swallows every
   tap, and the scroll lock stays on. Hence "on screen" below means actually
   visible, opacity included, and the check re-runs a few times after a close
   (a finished animation changes nothing an observer could see).

   Deliberately narrow: it never closes or opens anything, never touches the
   form, its fields or its submit. It only undoes a lock that outlived the
   thing that set it. A stranded layer is made click-through
   (pointer-events: none), not removed, and gets its clicks back if Klaviyo
   shows it again. */
(function () {
  var FORM_SELECTOR = "form[data-testid^='klaviyo-form']";
  var LOCK_CLASS = 'klaviyo-prevent-body-scrolling';
  var RELEASED = 'data-brand-kl-released';

  /* Visible to a person: nothing on the way up to <body> is display:none,
     visibility:hidden or faded out to (near) zero opacity. */
  function visible(el) {
    for (var e = el; e && e !== document.body; e = e.parentElement) {
      var cs = window.getComputedStyle(e);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
    }
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function modalOnScreen() {
    var forms = document.querySelectorAll(FORM_SELECTOR);
    for (var i = 0; i < forms.length; i++) { if (visible(forms[i])) return true; }
    return false;
  }

  /* A fixed layer big enough to cover the page, belonging to Klaviyo, with no
     visible modal inside it any more. */
  function isStrandedOverlay(el) {
    var cls = String(el.className || '');
    if (cls.indexOf('kl-') === -1 && cls.indexOf('klaviyo') === -1) return false;
    var cs = window.getComputedStyle(el);
    if (cs.position !== 'fixed' || cs.display === 'none') return false;
    var r = el.getBoundingClientRect();
    if (r.width < window.innerWidth * 0.9 || r.height < window.innerHeight * 0.9) return false;
    var form = el.querySelector(FORM_SELECTOR);
    return !form || !visible(form);
  }

  /* pointer-events: none on the layer alone is not enough: Klaviyo's reset
     CSS sets pointer-events back to auto on every element inside it, so the
     invisible popup box kept catching taps in the middle of the page. This
     rule reaches the whole stranded layer, including the ::before/::after
     strips Klaviyo draws above and below the box. */
  function addReleaseRule() {
    if (document.getElementById('brand-kl-release')) return;
    var s = document.createElement('style');
    var r = '[' + RELEASED + ']';
    s.id = 'brand-kl-release';
    s.textContent = r + ', ' + r + ' *, ' + r + ' *::before, ' + r + ' *::after { pointer-events: none !important; }';
    document.head.appendChild(s);
  }

  /* Klaviyo showing a popup again: hand its layers their clicks back. */
  function restoreReleased() {
    var done = document.querySelectorAll('[' + RELEASED + ']');
    for (var i = 0; i < done.length; i++) {
      done[i].style.pointerEvents = '';
      done[i].removeAttribute(RELEASED);
    }
  }

  function release() {
    if (modalOnScreen()) { restoreReleased(); return; }

    var body = document.body;
    if (!body) return;

    /* Cheap exit. The observer below fires on every direct child added to
       <body>, which during load is constantly, because every app mounts its
       widgets there. Each run used to reach the querySelectorAll on
       [class*="kl-"] at the bottom -- an attribute-substring match over the
       whole document -- whether or not Klaviyo had put anything on the page
       yet. Nothing here can have anything to undo unless the page is actually
       locked or a Klaviyo element exists, and querySelector stops at the first
       hit instead of collecting every node. Same outcome, a fraction of the
       work. */
    var locked = body.classList.contains(LOCK_CLASS)
      || body.style.overflow === 'hidden'
      || document.documentElement.style.overflow === 'hidden'
      || body.style.position === 'fixed';
    if (!locked && !document.querySelector('[class*="kl-"], [class*="klaviyo"]')) return;
    body.classList.remove(LOCK_CLASS);
    if (body.style.overflow === 'hidden') body.style.overflow = '';
    if (document.documentElement.style.overflow === 'hidden') {
      document.documentElement.style.overflow = '';
    }
    /* Some lock implementations pin the body with position:fixed and a
       negative top, which also loses the scroll position. */
    if (body.style.position === 'fixed') {
      var top = parseInt(body.style.top || '0', 10);
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      if (top < 0) window.scrollTo(0, -top);
    }

    var i;
    var layers = document.querySelectorAll('[class*="kl-"], [class*="klaviyo"]');
    for (i = 0; i < layers.length; i++) {
      if (isStrandedOverlay(layers[i])) {
        addReleaseRule();
        layers[i].style.pointerEvents = 'none';
        layers[i].setAttribute(RELEASED, '');
      }
    }
  }

  var pending = null;
  function scheduleRelease() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      release();
    }, 120);
  }

  /* After a close, check again once the fade-out has had time to finish.
     Nothing observable changes when it does, so the observer below would
     never fire for it. */
  function releaseAfterClose() {
    [150, 600, 1200, 2500].forEach(function (ms) { setTimeout(release, ms); });
  }

  window.addEventListener('klaviyoForms', function (e) {
    scheduleRelease();
    if (e && e.detail && e.detail.type === 'close') releaseAfterClose();
  });
  /* The close button, a tap on the backdrop and Escape all close it, so any
     of them while a Klaviyo form is on the page schedules the re-checks.
     release() itself never acts while the popup is still visible. */
  document.addEventListener('click', function () {
    if (document.querySelector(FORM_SELECTOR)) releaseAfterClose();
  }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.querySelector(FORM_SELECTOR)) releaseAfterClose();
  }, true);

  function watch() {
    if (!document.body) return;
    if (typeof MutationObserver === 'undefined') return;
    new MutationObserver(scheduleRelease).observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
